import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Phone, MessageCircle, ArrowLeft, ArrowUpRight, ShieldAlert, FileText,
  Receipt, ClipboardCheck, Info, AlertTriangle, Clock, Building2,
} from "lucide-react";
import SEO from "@/components/SEO";
import Header from "@/components/sections/Header";
import Reviews from "@/components/sections/Reviews";
import Footer from "@/components/sections/Footer";
import StickyCallBar from "@/components/StickyCallBar";
import WhatsAppFab from "@/components/WhatsAppFab";
import { PHONE_DISPLAY, PHONE_TEL_HREF, WHATSAPP_HREF } from "@/lib/contact";
import { track } from "@/lib/analytics";
import {
  localBusinessSchema, breadcrumbSchema, serviceSchema, faqPageSchema,
} from "@/lib/schemas";
import { SITE_URL } from "@/lib/site";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";
import { fadeUp, fadeUpStagger, IN_VIEW, EASE_OUT_EXPO } from "@/lib/motion";

const SLUG = "carro-attrezzi-incidente-assicurazione-roma";
const CANONICAL = `${SITE_URL}/${SLUG}`;

const TITLE =
  "Carroattrezzi Incidente Roma H24 · Fattura + Documentazione | 06 9763 6222";
const DESCRIPTION =
  "Incidente a Roma di notte e la tua assicurazione non risponde? Interveniamo H24 su tua richiesta, rilasciamo fattura e documentazione dell'intervento da presentare alla tua compagnia. Chiama 06 9763 6222.";
const KEYWORDS =
  "carro attrezzi incidente Roma, soccorso stradale incidente notturno Roma, fattura carro attrezzi assicurazione, documentazione intervento carro attrezzi, carro attrezzi assicurazione non risponde Roma";

const H1 = "Carro attrezzi per incidente a Roma — con fattura e documentazione completa";
const LEAD =
  "Hai avuto un incidente a Roma e la tua assicurazione non risponde o i tempi di attesa sono troppo lunghi? Interveniamo H24 direttamente su tua richiesta. Al termine ti rilasciamo fattura regolare e documentazione dettagliata dell'intervento, che potrai poi presentare alla tua compagnia per verificare — secondo le condizioni della tua polizza — un eventuale rimborso.";

const STEPS = [
  {
    n: "01",
    icon: Phone,
    title: "Chiami e ci descrivi la situazione",
    body:
      "Rispondiamo H24 al 06 9763 6222. Ti chiediamo la posizione esatta, il tipo di veicolo e cosa è successo, così arriviamo con il mezzo giusto e il tempo giusto.",
  },
  {
    n: "02",
    icon: Clock,
    title: "Arriviamo sul posto rapidamente",
    body:
      "Muoviamo il carro attrezzi disponibile più vicino a te, anche di notte, festivi e weekend. Ti diamo un tempo stimato realistico al telefono.",
  },
  {
    n: "03",
    icon: ClipboardCheck,
    title: "Eseguiamo il recupero e documentiamo",
    body:
      "Recuperiamo il veicolo in sicurezza e lo trainiamo dove decidi tu: officina di fiducia, carrozzeria, deposito o casa. Se lo chiedi, foto e note dell'intervento.",
  },
  {
    n: "04",
    icon: Receipt,
    title: "Fattura + documentazione consegnata",
    body:
      "Ti rilasciamo fattura fiscalmente valida con i dati dell'intervento (data, ora, luogo, tipologia, importo). Tu potrai poi verificare con la tua compagnia se — secondo la tua polizza — l'importo è rimborsabile.",
  },
];

const DOCUMENTS = [
  {
    icon: Receipt,
    title: "Fattura regolare",
    body:
      "Fattura fiscalmente valida intestata a te o alla tua azienda, con partita IVA emittente, importo, IVA e causale dell'intervento.",
  },
  {
    icon: FileText,
    title: "Dettaglio dell'intervento",
    body:
      "Sulla fattura o su documento accompagnatorio riportiamo data, ora, luogo di partenza e destinazione, tipologia di veicolo e servizio effettuato.",
  },
  {
    icon: ShieldAlert,
    title: "Dati identificativi del mezzo di soccorso",
    body:
      "Ragione sociale, sede, contatti e — se richiesti dalla tua assicurazione — targa e dati del carro attrezzi utilizzato.",
  },
  {
    icon: ClipboardCheck,
    title: "Foto dell'intervento (a richiesta)",
    body:
      "Se ce lo chiedi al momento della chiamata, possiamo fornirti alcune foto dell'operazione di recupero, utili se la tua compagnia le richiede.",
  },
];

const FAQS = [
  {
    q: "Se ho un incidente a Roma di notte e la mia assicurazione non risponde, posso chiamare voi direttamente?",
    a: "Sì. Rispondiamo 24 ore su 24 al 06 9763 6222. Il servizio viene erogato su tua richiesta diretta, senza passare dalla centrale operativa della tua compagnia. Al termine ricevi fattura regolare e la documentazione dell'intervento.",
  },
  {
    q: "La mia assicurazione mi rimborserà il costo del vostro carro attrezzi?",
    a: "Il rimborso non è mai automatico e non dipende da noi: ogni polizza ha condizioni proprie (massimali, franchigie, tipologia di sinistro, tempistiche di richiesta, obbligo di preventivo autorizzazione). Ti forniamo tutta la documentazione necessaria per farne richiesta: sarà la tua compagnia a valutare — in base al contratto che hai sottoscritto — se e in che misura riconoscerà il rimborso.",
  },
  {
    q: "Cosa ricevo alla fine dell'intervento?",
    a: "Ricevi una fattura fiscalmente valida con l'importo pagato, il dettaglio dell'intervento (data, ora, luogo, veicolo, tipo di servizio) e i nostri dati identificativi. Se lo richiedi, possiamo aggiungere foto del recupero.",
  },
  {
    q: "Devo prima chiamare la mia assicurazione oppure posso chiamare direttamente voi?",
    a: "Se la tua polizza include soccorso stradale, la procedura standard è contattare prima la tua compagnia: è la strada corretta quando funziona, perché in molti casi il servizio viene organizzato senza costi per te. Se però l'assistenza è irraggiungibile, i tempi sono troppo lunghi o preferisci muoverti in autonomia, puoi chiamare noi direttamente. Sarà poi tua responsabilità verificare con la tua compagnia le condizioni di eventuale rimborso.",
  },
  {
    q: "Posso decidere io dove far portare il veicolo?",
    a: "Sì. Trasportiamo l'auto o la moto dove indichi tu: officina di fiducia, carrozzeria, concessionaria, deposito o abitazione, nei limiti di percorrenza concordati al telefono.",
  },
  {
    q: "Che tipo di pagamento accettate?",
    a: "Accettiamo contanti e pagamento con carta direttamente sul posto tramite POS mobile. Nessun pagamento anticipato al telefono.",
  },
  {
    q: "Emettete fattura anche a partita IVA / azienda / flotta?",
    a: "Sì. Comunicaci al telefono i dati fiscali (ragione sociale, P.IVA, codice destinatario o PEC) ed emettiamo fattura elettronica intestata alla tua azienda.",
  },
];

export default function InsuranceAccidentPage() {
  const jsonLd = [
    localBusinessSchema(),
    breadcrumbSchema([
      { name: "Home", url: `${SITE_URL}/` },
      { name: "Carro attrezzi incidente & assicurazione", url: CANONICAL },
    ]),
    serviceSchema({
      name: "Carro attrezzi per incidente su richiesta cliente — Roma",
      description:
        "Servizio di carro attrezzi H24 a Roma su richiesta diretta del cliente, con rilascio di fattura fiscale e documentazione dettagliata dell'intervento, utili al cliente per verificare con la propria compagnia assicurativa un eventuale rimborso previsto dalla propria polizza.",
      url: CANONICAL,
    }),
    faqPageSchema(FAQS),
  ];

  return (
    <div
      className="min-h-screen bg-white text-navy"
      data-testid={`page-${SLUG}`}
    >
      <SEO
        title={TITLE}
        description={DESCRIPTION}
        canonical={CANONICAL}
        keywords={KEYWORDS}
        image={`${SITE_URL}/og-image.png`}
        jsonLd={jsonLd}
      />

      <Header />

      <main>
        {/* HERO */}
        <section
          data-testid="insurance-hero"
          className="relative isolate min-h-[86vh] flex items-end md:items-center overflow-hidden bg-navy grain"
        >
          <div className="absolute inset-0 -z-10 bg-gradient-to-br from-navy via-navy to-[color:var(--brand-navy-2)]" />
          <div className="absolute inset-0 -z-10 hero-grid opacity-25" />
          <div className="absolute -top-40 -right-40 h-[520px] w-[520px] rounded-full bg-brand-orange/20 blur-[160px]" />

          <div className="mx-auto max-w-7xl px-4 sm:px-6 pt-28 md:pt-32 pb-16 md:pb-24 w-full relative">
            <Link
              to="/"
              data-testid="insurance-back-link"
              className="inline-flex items-center gap-2 text-white/70 hover:text-white text-sm font-semibold"
            >
              <ArrowLeft className="h-4 w-4" /> Torna alla home
            </Link>

            <motion.div
              initial="hidden"
              animate="visible"
              variants={fadeUpStagger}
              className="mt-6 max-w-4xl"
            >
              <motion.span
                variants={fadeUp}
                className="inline-flex items-center gap-2 rounded-full bg-white/8 ring-1 ring-white/20 px-3 py-1.5 text-xs sm:text-sm font-semibold tracking-[0.22em] uppercase text-white/90 backdrop-blur"
              >
                <AlertTriangle className="h-3.5 w-3.5 text-brand-orange" />
                Incidente H24 · Roma & Provincia
              </motion.span>

              <motion.h1
                variants={fadeUp}
                data-testid="insurance-h1"
                className="mt-6 font-display font-black text-white text-shadow-strong text-4xl sm:text-6xl lg:text-[5.5rem] leading-[1.02] tracking-tight"
              >
                Incidente a Roma e{" "}
                <span className="text-brand-orange italic">
                  la tua assicurazione non risponde
                </span>
                ?
              </motion.h1>

              <motion.p
                variants={fadeUp}
                data-testid="insurance-lead"
                className="mt-6 text-white/85 text-base sm:text-xl max-w-3xl leading-relaxed"
              >
                {LEAD}
              </motion.p>

              <motion.div variants={fadeUp} className="mt-8 flex flex-col sm:flex-row gap-3 sm:gap-4">
                <a
                  href={PHONE_TEL_HREF}
                  onClick={() => track.phoneCall("insurance_hero")}
                  data-testid="insurance-call-button"
                  className="inline-flex items-center justify-center gap-3 rounded-2xl bg-brand-orange text-white font-bold px-6 py-4 text-lg sm:text-xl animate-pulse-ring btn-glow-orange hover:brightness-110 hover:-translate-y-0.5 transition"
                >
                  <Phone className="h-6 w-6" />
                  Chiama Ora
                  <span className="font-mono">{PHONE_DISPLAY}</span>
                </a>
                <a
                  href={WHATSAPP_HREF}
                  onClick={() => track.whatsapp("insurance_hero")}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="insurance-whatsapp-button"
                  className="inline-flex items-center justify-center gap-3 rounded-2xl bg-whatsapp text-white font-bold px-6 py-4 text-lg sm:text-xl btn-glow-green hover:brightness-110 hover:-translate-y-0.5 transition"
                >
                  <MessageCircle className="h-6 w-6" />
                  WhatsApp
                </a>
              </motion.div>

              <motion.p
                variants={fadeUp}
                className="mt-5 text-white/50 text-xs sm:text-sm font-mono tracking-wide"
              >
                Servizio erogato su richiesta diretta del cliente · Fattura e documentazione sempre incluse
              </motion.p>
            </motion.div>
          </div>
        </section>

        {/* STEPS — Come funziona */}
        <section
          id="come-funziona"
          data-testid="insurance-steps-section"
          className="relative py-20 md:py-28 bg-white"
        >
          <div className="mx-auto max-w-7xl px-4 sm:px-6">
            <motion.header
              initial="hidden"
              whileInView="visible"
              viewport={IN_VIEW}
              variants={fadeUpStagger}
              className="max-w-3xl"
            >
              <motion.p variants={fadeUp} className="text-brand-orange text-xs sm:text-sm font-bold tracking-[0.35em] uppercase">
                — Come funziona
              </motion.p>
              <motion.h2
                variants={fadeUp}
                className="mt-4 font-display font-black text-4xl sm:text-5xl lg:text-6xl text-navy tracking-tight leading-[1.02]"
              >
                Quattro passi, <span className="text-brand-orange italic">nessuna sorpresa</span>.
              </motion.h2>
              <motion.p
                variants={fadeUp}
                className="mt-5 text-muted-foreground text-base sm:text-lg leading-relaxed"
              >
                Dal momento in cui ci chiami al rilascio della fattura, sai sempre esattamente cosa aspettarti.
                Nessun pagamento anticipato al telefono, nessun costo nascosto in strada.
              </motion.p>
            </motion.header>

            <ol className="mt-14 grid gap-4 md:gap-5 md:grid-cols-2 lg:grid-cols-4">
              {STEPS.map((s, i) => (
                <motion.li
                  key={s.n}
                  initial={{ opacity: 0, y: 32 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={IN_VIEW}
                  transition={{ duration: 0.75, ease: EASE_OUT_EXPO, delay: 0.05 * i }}
                  data-testid={`insurance-step-${i}`}
                  className="relative rounded-2xl border border-border bg-white p-6 hover:shadow-lg hover:border-navy/20 transition"
                >
                  <span className="font-mono text-xs tracking-widest text-muted-foreground">{s.n}</span>
                  <div className="mt-3 grid place-items-center h-12 w-12 rounded-xl bg-navy text-white">
                    <s.icon className="h-6 w-6" />
                  </div>
                  <h3 className="mt-4 font-display font-extrabold text-lg text-navy leading-snug">
                    {s.title}
                  </h3>
                  <p className="mt-2 text-sm text-muted-foreground leading-relaxed">
                    {s.body}
                  </p>
                </motion.li>
              ))}
            </ol>
          </div>
        </section>

        {/* DOCUMENTS — cosa rilasciamo */}
        <section
          id="documentazione"
          data-testid="insurance-docs-section"
          className="relative py-20 md:py-28 bg-[color:var(--brand-surface)]"
        >
          <div className="mx-auto max-w-7xl px-4 sm:px-6 grid lg:grid-cols-12 gap-10 lg:gap-16 items-start">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={IN_VIEW}
              variants={fadeUpStagger}
              className="lg:col-span-5"
            >
              <motion.p variants={fadeUp} className="text-brand-orange text-xs sm:text-sm font-bold tracking-[0.35em] uppercase">
                — Cosa ricevi
              </motion.p>
              <motion.h2
                variants={fadeUp}
                className="mt-4 font-display font-black text-4xl sm:text-5xl text-navy tracking-tight leading-[1.02]"
              >
                Documentazione <span className="text-brand-orange italic">completa</span>,{" "}
                pronta per la tua assicurazione.
              </motion.h2>
              <motion.p
                variants={fadeUp}
                className="mt-5 text-muted-foreground text-base sm:text-lg leading-relaxed"
              >
                Al termine dell'intervento ricevi tutto ciò che serve per{" "}
                <strong className="text-navy">
                  fare richiesta di rimborso alla tua compagnia
                </strong>
                . Sarà poi la compagnia — in base alla tua polizza — a valutare l'esito.
              </motion.p>
              <motion.a
                variants={fadeUp}
                href={PHONE_TEL_HREF}
                onClick={() => track.phoneCall("insurance_docs")}
                data-testid="insurance-docs-call-button"
                className="mt-7 inline-flex items-center gap-3 rounded-2xl bg-navy text-white px-6 py-4 font-bold hover:bg-brand-orange transition-colors"
              >
                <Phone className="h-5 w-5" />
                Chiama {PHONE_DISPLAY}
              </motion.a>
            </motion.div>

            <motion.ul
              initial="hidden"
              whileInView="visible"
              viewport={IN_VIEW}
              variants={fadeUpStagger}
              className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4"
            >
              {DOCUMENTS.map((d, i) => (
                <motion.li
                  key={d.title}
                  variants={fadeUp}
                  data-testid={`insurance-doc-${i}`}
                  className="rounded-2xl bg-white border border-border p-5 hover:shadow-md hover:border-brand-orange/40 transition"
                >
                  <div className="grid place-items-center h-11 w-11 rounded-xl bg-brand-orange/10 text-brand-orange">
                    <d.icon className="h-5 w-5" />
                  </div>
                  <h3 className="mt-4 font-display font-extrabold text-lg text-navy">{d.title}</h3>
                  <p className="mt-1.5 text-sm text-muted-foreground leading-relaxed">
                    {d.body}
                  </p>
                </motion.li>
              ))}
            </motion.ul>
          </div>
        </section>

        {/* DISCLAIMER — trasparenza legale sul rimborso */}
        <section
          id="rimborso"
          data-testid="insurance-disclaimer-section"
          className="relative py-20 md:py-28 bg-navy text-white overflow-hidden grain"
        >
          <div className="absolute inset-0 hero-grid opacity-20 pointer-events-none" />
          <div className="mx-auto max-w-4xl px-4 sm:px-6 relative">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={IN_VIEW}
              variants={fadeUpStagger}
              className="text-center"
            >
              <motion.div
                variants={fadeUp}
                className="mx-auto grid place-items-center h-14 w-14 rounded-2xl bg-brand-orange/15 text-brand-orange ring-1 ring-brand-orange/30"
              >
                <Info className="h-7 w-7" />
              </motion.div>
              <motion.p variants={fadeUp} className="mt-6 text-brand-orange text-xs sm:text-sm font-bold tracking-[0.35em] uppercase">
                — Trasparenza sul rimborso
              </motion.p>
              <motion.h2
                variants={fadeUp}
                className="mt-4 font-display font-black text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-[1.05]"
              >
                Non promettiamo mai un rimborso.{" "}
                <span className="text-brand-orange italic">Ti diamo gli strumenti</span> per chiederlo.
              </motion.h2>

              <motion.div
                variants={fadeUp}
                className="mt-8 text-left sm:text-center text-white/80 text-base sm:text-lg leading-relaxed space-y-4 max-w-3xl mx-auto"
              >
                <p>
                  Il nostro è un servizio di carro attrezzi erogato{" "}
                  <strong className="text-white">su tua richiesta diretta</strong>.
                  Ti forniamo{" "}
                  <strong className="text-white">
                    fattura fiscalmente valida
                  </strong>{" "}
                  e la documentazione dettagliata dell'intervento, che puoi
                  poi presentare alla tua compagnia assicurativa.
                </p>
                <p>
                  L'eventuale rimborso da parte della tua assicurazione{" "}
                  <strong className="text-white">
                    non è mai automatico
                  </strong>{" "}
                  e non dipende da noi: è regolato esclusivamente dalle
                  condizioni della tua polizza (massimali, franchigie,
                  tipologia di sinistro coperto, obbligo o meno di previa
                  autorizzazione, tempistiche di denuncia).
                </p>
                <p>
                  Ti invitiamo sempre a{" "}
                  <strong className="text-white">
                    verificare con la tua compagnia
                  </strong>{" "}
                  quali procedure prevede la tua polizza e a seguire le
                  indicazioni ufficiali del tuo assicuratore per la richiesta
                  di rimborso.
                </p>
              </motion.div>
            </motion.div>
          </div>
        </section>

        {/* WHY — perché un cliente ci chiama in questi casi (differenza di posizionamento) */}
        <section
          data-testid="insurance-why-section"
          className="relative py-20 md:py-28 bg-white"
        >
          <div className="mx-auto max-w-7xl px-4 sm:px-6 grid lg:grid-cols-2 gap-10 lg:gap-16 items-start">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={IN_VIEW}
              variants={fadeUpStagger}
            >
              <motion.p variants={fadeUp} className="text-brand-orange text-xs sm:text-sm font-bold tracking-[0.35em] uppercase">
                — Quando ci chiamano
              </motion.p>
              <motion.h2
                variants={fadeUp}
                className="mt-4 font-display font-black text-4xl sm:text-5xl text-navy tracking-tight leading-[1.02]"
              >
                Le situazioni <span className="text-brand-orange italic">più frequenti</span>.
              </motion.h2>
              <motion.p variants={fadeUp} className="mt-5 text-muted-foreground text-base sm:text-lg leading-relaxed">
                Non sostituiamo la tua assicurazione: interveniamo quando ne
                hai bisogno adesso e la procedura standard non è praticabile in tempi utili.
              </motion.p>
            </motion.div>

            <motion.ul
              initial="hidden"
              whileInView="visible"
              viewport={IN_VIEW}
              variants={fadeUpStagger}
              className="grid gap-3"
            >
              {[
                {
                  icon: Clock,
                  title: "Assistenza assicurativa irraggiungibile",
                  body: "Numero verde occupato, attese lunghe, centralino chiuso: intervieniamo noi H24.",
                },
                {
                  icon: AlertTriangle,
                  title: "Incidente notturno o festivo",
                  body: "In orari in cui l'operativo della compagnia è ridotto, arriviamo con tempi rapidi.",
                },
                {
                  icon: Building2,
                  title: "Cliente aziendale o flotta",
                  body: "Serve fattura intestata all'azienda in tempi brevi, con tutti i dati per la richiesta interna.",
                },
                {
                  icon: ShieldAlert,
                  title: "Polizza senza soccorso stradale",
                  body: "Se la tua polizza non include il carro attrezzi, ti serve un fornitore diretto: siamo noi.",
                },
              ].map((item, i) => (
                <motion.li
                  key={item.title}
                  variants={fadeUp}
                  data-testid={`insurance-when-${i}`}
                  className="flex items-start gap-4 rounded-2xl border border-border bg-white p-5"
                >
                  <div className="grid place-items-center h-11 w-11 rounded-xl bg-navy text-white shrink-0">
                    <item.icon className="h-5 w-5" />
                  </div>
                  <div>
                    <h3 className="font-display font-extrabold text-lg text-navy">{item.title}</h3>
                    <p className="mt-1 text-sm text-muted-foreground leading-relaxed">{item.body}</p>
                  </div>
                </motion.li>
              ))}
            </motion.ul>
          </div>
        </section>

        {/* FAQ */}
        <section
          id="faq"
          data-testid="insurance-faq-section"
          className="relative py-20 md:py-28 bg-[color:var(--brand-surface)]"
        >
          <div className="mx-auto max-w-5xl px-4 sm:px-6">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={IN_VIEW}
              variants={fadeUpStagger}
              className="text-center max-w-2xl mx-auto"
            >
              <motion.p variants={fadeUp} className="text-brand-orange text-xs sm:text-sm font-bold tracking-[0.35em] uppercase">
                — Domande frequenti
              </motion.p>
              <motion.h2
                variants={fadeUp}
                className="mt-4 font-display font-black text-4xl sm:text-5xl text-navy tracking-tight leading-[1.02]"
              >
                Risposte chiare su <span className="text-brand-orange italic">incidente e assicurazione</span>.
              </motion.h2>
            </motion.div>

            <Accordion
              type="single"
              collapsible
              className="mt-10 divide-y divide-border border border-border rounded-2xl bg-white"
              data-testid="insurance-faq-accordion"
            >
              {FAQS.map((f, i) => (
                <AccordionItem key={i} value={`item-${i}`} className="px-5 sm:px-7">
                  <AccordionTrigger
                    data-testid={`insurance-faq-trigger-${i}`}
                    className="text-left font-display font-bold text-navy text-base sm:text-lg hover:no-underline"
                  >
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground text-base leading-relaxed pb-5">
                    {f.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>

            <div className="mt-10 text-center">
              <p className="text-muted-foreground">Situazione urgente? Non aspettare.</p>
              <a
                href={PHONE_TEL_HREF}
                onClick={() => track.phoneCall("insurance_faq")}
                data-testid="insurance-faq-call-button"
                className="mt-3 inline-flex items-center gap-3 rounded-2xl bg-navy text-white px-6 py-4 font-bold hover:bg-brand-orange transition-colors"
              >
                <Phone className="h-5 w-5" />
                Chiama {PHONE_DISPLAY}
              </a>
            </div>
          </div>
        </section>

        <Reviews />
      </main>

      <Footer />
      <WhatsAppFab />
      <StickyCallBar />
      <div aria-hidden className="h-20 md:h-0" />
    </div>
  );
}
